/* eslint-disable camelcase */
import { Props as MenuFeedItemProps } from '../components/menus/menu-feed-item';
import { Response } from '../services/__fixtures__/service-fixtures';
import pluralize from '../utils/pluralize';
import DateRange from './date-range';
import DateF from './date-f';

export type Resource = 'record';
type Status = 'approved';
export type ActionType = 'delete'|'update';
export type ProcessStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'cancelled';
export type LocalResponse = BatchFetchResponse | BatchStorage[] | undefined;

export type DataType = {
  status: Status;
};

export type Action = {
  type: ActionType;
  description: string;
}

type Operation = {
  action: ActionType;
  resource: Resource;
  id?: string;
  form_id?: string;
  ids?: string[];
  query?: string;
  data?: DataType | Record<string, any>;
};

export type BatchResponse = {
  status: ProcessStatus;
  objects_total: number;
  objects_success: number;
  id: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  created_by_id: string;
  updated_by: string;
  updated_by_id: string;
  started_at?: string;
  completed_at?: string;
  form_ids?: string[];
  actions: Action[];
}

export type BatchRequestOption = {
  activity: boolean;
}

export type BatchPostMutation = {
  start: string;
  operations: Operation[];
  formName?: string;
  formId?: string;
  tempID?: string;
};

export interface BatchStorage extends MenuFeedItemProps {
  completedAt?: string;
  startedAt?: string;
  formId?: string;
}

export interface BatchFetchResponse extends Partial<Response> {
  batches: BatchResponse[];
}

export interface BatchPostResponse extends Partial<Response> {
  batch: BatchResponse;
}
export interface BatchPostErrorResponse extends Partial<Response> {
  data: { // fix this repetitive interface
    error: {
      data: {
        error: {
          batch: BatchResponse;
          status: number;
        };
      };
    };
  };
}

export enum ActionMap {
  'delete' = 'deletion'
}

function getElapsedTime(start: string, updated: string) {
  const startTime = new Date(start).getTime();
  const endTime = new Date(updated).getTime();
  const elapsed = endTime - startTime;

  if (elapsed === 0) {
    return elapsed;
  }

  const h = Math.floor(elapsed / 36e5);
  const m = Math.floor((elapsed % 36e5) / 60000);
  const s = Math.floor((elapsed % 60000) / 1000);
  let hoursComma = '';
  let minutesComma = '';

  if (m) {
    hoursComma = ',';
  }
  if (s) {
    minutesComma = ',';
  }

  return `${h ? `${pluralize(h, 'hour')}${hoursComma} ` : ''}${
    m ? `${pluralize(m, 'minute')}${minutesComma} ` : ''
  }${s ? `${pluralize(s, 'second')}` : ''}`;
}

export function renderCaption(status: ProcessStatus, start: string, type: ActionType = 'delete') {
  const currentTime = new Date().toISOString();
  const dateRange = new DateRange([new DateF(start), new DateF(currentTime)]);
  const captionTextMap = {
    delete: { errorVerb: 'delete' },
    update: { errorVerb: 'change' },
  };

  switch (status) {
    case 'running':
      return `${getElapsedTime(start, currentTime) === 0
        ? 'Processing...' : `Elapsed time ${getElapsedTime(start, currentTime)}`}`;
    case 'succeeded':
      return `Completed ${dateRange.toTimeElapsedPhrase}`;
    case 'failed':
      return `Error: some records failed to ${captionTextMap[type].errorVerb}`;
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'Processing...';
  }
}

export function calculateProgress(current: number, total: number) {
  return Math.round((100 * current) / total);
}

export function updateCompletedTimestamp(operations: BatchStorage[]) {
  if (!operations.length) {
    return operations;
  }

  return operations.map((operation) => {
    if (operation.status === 'succeeded') {
      const timeAgoInWords = new DateRange([
        new DateF(operation.completedAt), new DateF(),
      ]).toTimeElapsedPhrase;

      return {
        ...operation,
        caption: `Completed ${timeAgoInWords}`,
      };
    }
    return operation;
  });
}

export function renderPrimaryText(
  status: ProcessStatus,
  current: number,
  total: number,
  type: ActionType = 'delete',
) {
  const primaryTextMap = {
    delete: { actionText: 'Deleting', pastTenseText: 'deleted', startText: 'deletion' },
    update: { actionText: 'Changing', pastTenseText: 'changed', startText: 'change' },
  };

  switch (status) {
    case 'running':
      return `${primaryTextMap[type].actionText} ${current?.toLocaleString()} of ${pluralize(total, 'record')}`;
    case 'succeeded':
      return `${pluralize(current, 'record')} ${primaryTextMap[type].pastTenseText}`;
    case 'failed':
      return `${current?.toLocaleString()} of ${pluralize(total, 'record')} ${primaryTextMap[type].pastTenseText}`;
    default:
      return `Starting the ${primaryTextMap[type].startText} process`;
  }
}
